import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { breakpoints, Button, Dialog, Input, Stack, vars } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { TripType, type TripType as TripTypeValue } from '@eti/schema-types';
import { addDays, formatISO, startOfMonth, startOfDay, add } from 'date-fns';
import { useLazyQuery } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { useMediaQuery } from 'usehooks-ts';
import { MULTI_STOP } from '../../../constants/tripTypesConstants';
import {
  DEPARTURE_DATE,
  DESTINATION,
  MULTI_BOUND,
  ORIGIN,
  RETURN_DATE,
  SINGLE_BOUND,
} from '../constants/formFields';
import { formatGetDate } from '../utils/format';
import { generateDurationText } from '../utils/calendarDateUtils';
import { GET_PRICES_PER_DAY } from '../graphql/queries';
import type { DateFormat } from '../types/dateFormat';
import { eventKeys } from '../constants/eventKeys';
import { getFirstAndLastSelectableDateButtons } from '../utils/getFirstAndLastSelectableDateButtons';
import MobileCalendar from './calendar/MobileCalendar';
import DateInputNew from './DateInputNew';
import CalendarButton from './CalendarButton';
import ClickAwayListener from './clickAwayListener/ClickAwayListener';
import CalendarNew from './calendar/CalendarNew';

const datesWrapperStyles = css`
  flex: 1;
  position: relative;
`;

const calendarWrapperStyles = (isMultiStop: boolean) => css`
  margin-top: -1px;
  ${isMultiStop && 'inset-inline-end: 0'};
  min-width: fit-content;
  position: absolute;
  top: 100%;
  width: 100%;
  z-index: 9;

  @media (min-width: ${breakpoints._768}) and (max-width: ${breakpoints._970}) {
    min-width: 320px;
  }
`;

const dialogStyles = css`
  & > * > :last-child {
    margin-top: auto;
  }
`;

const datesButtonStyles = css`
  align-items: center;
  appearance: none;
  background-color: ${vars.colors.surface.primary};
  border-color: ${vars.colors.inputs.border};
  border-radius: 0;
  border-style: solid;
  border-width: 1px;
  color: inherit;
  display: flex;
  font-family: inherit;
  font-size: 1rem;
  height: 3rem;
  justify-content: space-between;
  outline: none;
  padding-inline: 12px 10px;
  text-align: start;
  width: 100%;

  &[disabled] {
    background-color: ${vars.colors.inputs.disabledBackground};
    cursor: not-allowed;
  }

  &:focus {
    border-inline-end: 1px solid ${vars.colors.inputs.main};
    border-radius: 3px;
  }

  & *:not(:last-child) &:focus {
    padding-inline-end: 0;
  }
`;

const buttonsDatesWrapperStyles = css`
  & > *:first-child .calendarTrigger {
    border-end-start-radius: 3px;
    border-start-start-radius: 3px;
  }

  & > *:last-child .calendarTrigger {
    border-end-end-radius: 3px;
    border-start-end-radius: 3px;
  }

  & > *:not(:last-child) .calendarTrigger {
    border-inline-end: none;
    padding-inline-end: 1px;
  }
`;

const buttonWrapperStyles = css`
  display: flex;
  position: relative;

  &::before {
    border-color: ${vars.colors.inputs.focus};
    border-radius: 6px;
    border-style: solid;
    border-width: 3px;
    content: '';
    height: calc(100% + 6px);
    left: -3px;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: -3px;
    transition: opacity 0.2s ease-in-out;
    width: calc(100% + 6px);
  }

  &:focus-within {
    z-index: 1;
  }

  &:focus-within::before {
    opacity: 1;
  }
`;

const datesPlaceholderStyles = css`
  color: ${vars.colors.text.secondary};
`;

const dialogFooterDividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  margin-inline: -16px;
  transform: translateY(-16px);
  width: calc(100% + 32px);
`;

const today = startOfDay(new Date());
const yesterday = addDays(today, -1);
const nextYear = add(today, { years: 1, days: 1 });

export interface DatesNewProps {
  boundIndex: number;
  dateFormat: DateFormat;
  departureDateName: string;
  selectedTripType?: TripType;
  departureInputRef?: RefObject<HTMLInputElement>;
}

const DatesNew = ({
  boundIndex,
  departureDateName,
  dateFormat,
  selectedTripType,
  departureInputRef,
}: DatesNewProps) => {
  const { setValue, getValues, watch } = useFormContext();

  const [originField, destinationField, bounds, departureDate, returnDate] = watch([
    `${SINGLE_BOUND}.${ORIGIN}`,
    `${SINGLE_BOUND}.${DESTINATION}`,
    `${MULTI_BOUND}`,
    `${SINGLE_BOUND}.${DEPARTURE_DATE}`,
    `${SINGLE_BOUND}.${RETURN_DATE}`,
  ]);
  const origin = originField?.iataCode;
  const destination = destinationField?.iataCode;
  const previousBoundDepartureDate = bounds
    ? watch(`${MULTI_BOUND}[${boundIndex - 1}].${DEPARTURE_DATE}`)
    : null;
  const { t } = useTranslation();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const [currentField, setCurrentField] = useState<
    typeof DEPARTURE_DATE | typeof RETURN_DATE | null
  >(null);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [shouldAutoFocusDay, setShouldAutoFocusDay] = useState(true);
  const { p } = useProperty();
  const isMultiStop = selectedTripType === TripType.MultiStop;
  const isPriceCalendarEnabled = p('IbeClient.Price.Calendar.Enabled');
  const isPriceCalendarMobileEnabled = p('IbeClient.Price.Calendar.Mobile.Enabled');
  const shouldShowReturnDateField = selectedTripType
    ? (
        [
          TripType.Return,
          TripType.OpenJawDouble,
          TripType.OpenJawSingleDestination,
          TripType.OpenJawSingleOrigin,
        ] as TripTypeValue[]
      ).includes(selectedTripType)
    : false;

  const hideCalendar = useCallback(() => {
    document
      .querySelector<HTMLButtonElement>(`button#calendar-icon-${currentField}-${boundIndex}`)
      ?.focus();
    setCurrentField(null);
    setIsCalendarVisible(false);
  }, [boundIndex, currentField]);

  const [getPriceData, { data, loading }] = useLazyQuery(GET_PRICES_PER_DAY, {
    fetchPolicy: 'no-cache',
  });

  const priceData = useMemo(
    () => [
      ...(data?.from0To57Days?.bestPricesPerDay ?? []),
      ...(data?.from57To114Days?.bestPricesPerDay ?? []),
      ...(data?.from114To171Days?.bestPricesPerDay ?? []),
      ...(data?.from171To228Days?.bestPricesPerDay ?? []),
      ...(data?.from228To285Days?.bestPricesPerDay ?? []),
      ...(data?.from285To342Days?.bestPricesPerDay ?? []),
      ...(data?.from342To399Days?.bestPricesPerDay ?? []),
    ],
    [data],
  );

  useEffect(() => {
    if (!isPriceCalendarEnabled || !origin || !destination || origin === destination) {
      return;
    }

    const startOfCurrentMonth = startOfMonth(today);

    const formatDate = (date: Date) => formatISO(date, { representation: 'date' });

    const NUMBER_OF_DAYS = 57;

    getPriceData({
      variables: {
        origin,
        destination,
        numberOfDays: NUMBER_OF_DAYS,
        startDate1: formatDate(startOfCurrentMonth),
        startDate2: formatDate(addDays(startOfCurrentMonth, NUMBER_OF_DAYS)),
        startDate3: formatDate(addDays(startOfCurrentMonth, NUMBER_OF_DAYS * 2)),
        startDate4: formatDate(addDays(startOfCurrentMonth, NUMBER_OF_DAYS * 3)),
        startDate5: formatDate(addDays(startOfCurrentMonth, NUMBER_OF_DAYS * 4)),
        startDate6: formatDate(addDays(startOfCurrentMonth, NUMBER_OF_DAYS * 5)),
        startDate7: formatDate(addDays(startOfCurrentMonth, NUMBER_OF_DAYS * 6)),
      },
    });
  }, [destination, getPriceData, isPriceCalendarEnabled, origin]);

  const getDateRange = () => {
    const singleBoundPath = `${SINGLE_BOUND}.${DEPARTURE_DATE}`;
    const multiBoundPath = `${MULTI_BOUND}[${boundIndex}].${DEPARTURE_DATE}`;

    const lastBoundDeparture = getValues(
      selectedTripType === MULTI_STOP ? multiBoundPath : singleBoundPath,
    );

    return {
      departureDate: lastBoundDeparture,
      returnDate,
    };
  };

  const dateRange = getDateRange();

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const previousButton = document.querySelector<HTMLButtonElement>(
        'button[data-month-button="previous"]:not([aria-disabled="true"])',
      );

      const nextButton = document.querySelector<HTMLButtonElement>(
        'button[data-month-button="next"]:not([aria-disabled="true"])',
      );

      const isNextButtonFocused = nextButton === document.activeElement;
      const isPreviousButtonFocused = previousButton === document.activeElement;

      const dateButtons = getFirstAndLastSelectableDateButtons(dateRange);

      if (!currentField || !dateButtons[currentField] || !isCalendarVisible) {
        return;
      }

      const dateButton = dateButtons[currentField];

      const isShiftTab = event.shiftKey && event.key === eventKeys.TAB;
      const isTab = !event.shiftKey && event.key === eventKeys.TAB;
      const isEscape = event.key === eventKeys.ESCAPE;

      if (isShiftTab) {
        event.preventDefault();
        if (isNextButtonFocused) {
          (previousButton ?? dateButton).focus();
          return;
        }

        if (isPreviousButtonFocused) {
          dateButton.focus();
          return;
        }

        (nextButton ?? previousButton)?.focus();

        return;
      }

      if (isTab) {
        event.preventDefault();
        if (isPreviousButtonFocused) {
          (nextButton ?? dateButton).focus();
          return;
        }

        if (isNextButtonFocused) {
          dateButton.focus();
          return;
        }

        (previousButton ?? nextButton)?.focus();

        return;
      }

      if (isEscape) {
        hideCalendar();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [currentField, dateRange, hideCalendar, isCalendarVisible]);

  useEffect(() => {
    setShouldAutoFocusDay(!isCalendarVisible);
  }, [isCalendarVisible]);

  const handleInputFocus = (field: typeof DEPARTURE_DATE | typeof RETURN_DATE) => () => {
    setIsCalendarVisible(false);
    setCurrentField(field);
  };

  const handleDepartureDateChange = (day: Date) => {
    setValue(departureDateName, day, { shouldValidate: true });
    hideCalendar();
  };

  const handleReturnDateChange = (day: Date) => {
    setValue(`${SINGLE_BOUND}.${RETURN_DATE}`, day);
    hideCalendar();
  };

  const calendarButtonClick = (fieldName: typeof DEPARTURE_DATE | typeof RETURN_DATE) => {
    if (isCalendarVisible && currentField !== fieldName) {
      setCurrentField(fieldName);
      return;
    }
    setCurrentField(isCalendarVisible ? null : fieldName);
    setIsCalendarVisible((prev) => !prev);
  };

  const previousBoundDeparture =
    previousBoundDepartureDate instanceof Date ? previousBoundDepartureDate : undefined;

  return (
    <ClickAwayListener
      className={datesWrapperStyles}
      isActive={isCalendarVisible && isWiderViewport}
      onClickAway={hideCalendar}
    >
      {isWiderViewport || !isPriceCalendarMobileEnabled ? (
        <>
          <Stack spacing={4}>
            <Input.Group>
              <div style={{ flex: 1 }}>
                <DateInputNew
                  afterDate={yesterday}
                  aria-label={t('ScreenReader.SearchForm.InputField.Depart')}
                  beforeDate={nextYear}
                  iconSuffix={
                    <CalendarButton
                      aria-controls={`${departureDateName}-calendar`}
                      aria-expanded={isCalendarVisible && currentField === DEPARTURE_DATE}
                      aria-label={t('ScreenReader.Calendar.OpenButton.Depart')}
                      boundIndex={boundIndex}
                      fieldName={DEPARTURE_DATE}
                      onClick={calendarButtonClick}
                    />
                  }
                  label={t('Searchform.DepartureDate.Label')}
                  name={departureDateName}
                  onFocus={handleInputFocus(DEPARTURE_DATE)}
                  placeholder={t('General.Departure')}
                  ref={departureInputRef}
                />
              </div>
              {shouldShowReturnDateField && (
                <div style={{ flex: 1 }}>
                  <DateInputNew
                    afterDate={addDays(departureDate, -1)}
                    aria-label={t('ScreenReader.SearchForm.InputField.Return')}
                    beforeDate={nextYear}
                    iconSuffix={
                      <CalendarButton
                        aria-controls={`${departureDateName}-calendar`}
                        aria-expanded={isCalendarVisible && currentField === RETURN_DATE}
                        aria-label={t('ScreenReader.Calendar.OpenButton.Return')}
                        boundIndex={boundIndex}
                        fieldName={RETURN_DATE}
                        onClick={calendarButtonClick}
                      />
                    }
                    label={t('Searchform.ReturnDate.Label')}
                    name={`${SINGLE_BOUND}.${RETURN_DATE}`}
                    onFocus={handleInputFocus(RETURN_DATE)}
                    placeholder={t('General.Return')}
                  />
                </div>
              )}
            </Input.Group>
          </Stack>

          {isCalendarVisible && (
            <div className={calendarWrapperStyles(isMultiStop)}>
              <CalendarNew
                bestPricesPerDay={priceData}
                boundIndex={boundIndex}
                currentField={currentField}
                daysFromTodayForValidDepartureDate={0}
                id={`${departureDateName}-calendar`}
                loading={loading}
                onDepartureNewDateChange={handleDepartureDateChange}
                onReturnNewDateChange={handleReturnDateChange}
                previousBoundDepartureDate={previousBoundDeparture}
                range={dateRange}
                selectedTripType={selectedTripType}
                shouldAutoFocusDay={shouldAutoFocusDay}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <Stack spacing={4}>
            <Input.Group className={buttonsDatesWrapperStyles}>
              <Stack spacing={4} style={{ width: '100%' }}>
                <Input.Label htmlFor={departureDateName}>
                  {t('Searchform.DepartureDate.Label')}
                </Input.Label>
                <div className={buttonWrapperStyles}>
                  <button
                    className={cx(datesButtonStyles, 'calendarTrigger')}
                    onClick={() => {
                      handleInputFocus(DEPARTURE_DATE);
                      calendarButtonClick(DEPARTURE_DATE);
                    }}
                    type="button"
                  >
                    {dateRange.departureDate ? (
                      formatGetDate(dateFormat)(dateRange.departureDate)
                    ) : (
                      <span className={datesPlaceholderStyles}>{t('General.Departure')}</span>
                    )}
                  </button>
                </div>
              </Stack>

              {selectedTripType !== TripType.MultiStop && selectedTripType !== TripType.OneWay && (
                <Stack spacing={4} style={{ width: '100%' }}>
                  <Input.Label htmlFor={`${SINGLE_BOUND}.${RETURN_DATE}`}>
                    {t('Searchform.ReturnDate.Label')}
                  </Input.Label>
                  <div className={buttonWrapperStyles}>
                    <button
                      className={cx(datesButtonStyles, 'calendarTrigger')}
                      onClick={() => {
                        handleInputFocus(RETURN_DATE);
                        calendarButtonClick(RETURN_DATE);
                      }}
                      type="button"
                    >
                      {dateRange.returnDate ? (
                        formatGetDate(dateFormat)(dateRange.returnDate)
                      ) : (
                        <span className={datesPlaceholderStyles}>{t('General.Return')}</span>
                      )}
                    </button>
                  </div>
                </Stack>
              )}
            </Input.Group>
          </Stack>

          <Dialog
            className={dialogStyles}
            footer={
              <>
                <div className={dialogFooterDividerStyles} />
                <Stack>
                  <Stack spacing={4}>
                    <Input.Group>
                      <DateInputNew
                        afterDate={yesterday}
                        beforeDate={nextYear}
                        label={t('Searchform.DepartureDate.Label')}
                        name={departureDateName}
                        onFocus={handleInputFocus(DEPARTURE_DATE)}
                        placeholder={t('General.Departure')}
                      />

                      {selectedTripType !== TripType.MultiStop &&
                        selectedTripType !== TripType.OneWay && (
                          <DateInputNew
                            afterDate={addDays(departureDate, -1)}
                            aria-label={t('ScreenReader.SearchForm.InputField.Return')}
                            beforeDate={nextYear}
                            label={t('Searchform.ReturnDate.Label')}
                            name={`${SINGLE_BOUND}.${RETURN_DATE}`}
                            onFocus={handleInputFocus(RETURN_DATE)}
                            placeholder={t('General.Return')}
                          />
                        )}
                    </Input.Group>
                  </Stack>
                  <Button data-testid="passengers-confirm" onClick={hideCalendar} variant="primary">
                    {generateDurationText(
                      dateRange.departureDate,
                      dateRange.returnDate,
                      t('Searchform.Calendar.Confirm.Singular'),
                      t('Searchform.Calendar.Confirm.Plural'),
                    )}
                  </Button>
                </Stack>
              </>
            }
            isOpen={isCalendarVisible}
            onDismiss={hideCalendar}
            title={t('Searchform.Calendar.DialogTitle.Text')}
          >
            <MobileCalendar
              bestPricesPerDay={priceData}
              currentField={currentField}
              daysFromTodayForValidDepartureDate={0}
              id={`${departureDateName}-calendar`}
              loading={loading}
              onDepartureNewDateChange={handleDepartureDateChange}
              onReturnNewDateChange={handleReturnDateChange}
              previousBoundDepartureDate={previousBoundDeparture}
              range={dateRange}
              selectedTripType={selectedTripType}
            />
          </Dialog>
        </>
      )}
    </ClickAwayListener>
  );
};

export default DatesNew;
